import { DemoVideo } from "@/components/landing/DemoVideo";
import Link from "@/components/school/SchoolLink";
import { SpeakingDemoIframe } from "@/components/seo/SpeakingDemoIframe";
import type { BlogPost } from "@/lib/blog";
import {
  resolveBlogArticleDemoVideo,
  resolveBlogArticleLiveDemo,
  resolveBlogArticleSchemaDetails,
  resolveBlogArticleSecondaryCta,
  resolveBlogEditorialCrossLinks,
} from "@/lib/seo/blogArticleSeo";
import { extractFaqsFromMarkdown } from "@/lib/seo/blogFaq";
import { formatBlogPostCtaLabel, resolveBlogPostLanguage } from "@/lib/seo/blogPostLanguage";
import { resolveBlogProductBridge } from "@/lib/seo/blogProductBridge";
import { getBlogRelatedPracticeLinks } from "@/lib/seo/blogRelatedPracticeLinks";
import { buildBreadcrumbListJsonLd, buildFaqPageJsonLd, serializeJsonLd } from "@/lib/seo/jsonLd";
import { cn } from "@/lib/utils";
import { ArrowRight, ArrowUpRight, CalendarDays, Check, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

export function formatBlogDate(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toBlogDateTime(value: string | null | undefined, fallbackDate: string): string {
  const source = value || fallbackDate;
  const date = source.includes("T") ? new Date(source) : new Date(`${source}T00:00:00.000Z`);

  if (Number.isNaN(date.getTime())) {
    return `${fallbackDate}T00:00:00.000Z`;
  }

  return date.toISOString();
}

export function BlogPageFrame({
  children,
  maxWidth = "max-w-6xl",
  className,
}: {
  children: ReactNode;
  maxWidth?: string;
  className?: string;
}) {
  return (
    <main className={cn("min-h-screen bg-[#fbfaf7] text-gray-950", className)}>
      <section className={cn("mx-auto px-5 py-10 sm:px-6 lg:px-8", maxWidth)}>{children}</section>
    </main>
  );
}

export function BlogBackLink({
  href = "/blog",
  label = "Back to blog",
}: {
  href?: string;
  label?: string;
}) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-orange-700"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
      {label}
    </Link>
  );
}

export function BlogHero({
  eyebrow = "ChickyTutor Blog",
  title,
  description,
  actions,
  rightSlot,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
  rightSlot?: ReactNode;
}) {
  return (
    <header className="rounded-lg border border-orange-100 bg-[#fff9f1] p-6 shadow-sm sm:p-8">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-orange-700">
            <Sparkles className="h-3.5 w-3.5" />
            {eyebrow}
          </div>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight text-gray-950 sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-gray-600 sm:text-lg">
            {description}
          </p>
          {actions ? <div className="mt-6 flex flex-wrap items-center gap-3">{actions}</div> : null}
        </div>
        {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
      </div>
    </header>
  );
}

export function BlogStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-950">{value}</p>
    </div>
  );
}

export function BlogChipLink({
  href,
  label,
  active = false,
}: {
  href: string;
  label: string;
  active?: boolean;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-orange-500 bg-orange-50 text-orange-700"
          : "border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:text-orange-700"
      )}
    >
      {label}
    </Link>
  );
}

export function BlogPostCard({
  post,
  href,
  className,
}: {
  post: BlogPost;
  href: string;
  className?: string;
}) {
  const displayLanguage = resolveBlogPostLanguage(post);

  return (
    <Link
      href={href}
      className={cn(
        "group block rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md",
        className
      )}
    >
      <div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
          <span>{displayLanguage}</span>
          <span className="h-1 w-1 rounded-full bg-gray-300" />
          <span>{post.category.replace("-", " ")}</span>
        </div>
        <h2 className="mt-4 text-xl font-semibold leading-tight text-gray-950 transition-colors group-hover:text-orange-700">
          {post.title}
        </h2>
        <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-600">{post.description}</p>
        <div className="mt-5 flex items-center justify-between text-xs font-medium text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {formatBlogDate(post.date)}
          </span>
          <span className="inline-flex items-center gap-1 font-semibold text-orange-700">
            Read
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

const blogMarkdownComponents: Components = {
  h1: ({ node: _node, ...props }) => (
    <h1 className="mt-8 font-serif text-3xl font-semibold text-gray-950" {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 className="mt-8 font-serif text-2xl font-semibold text-gray-950" {...props} />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 className="mt-6 text-xl font-semibold text-gray-950" {...props} />
  ),
  p: ({ node: _node, ...props }) => <p className="mt-4 leading-8 text-gray-700" {...props} />,
  strong: ({ node: _node, ...props }) => (
    <strong className="font-semibold text-gray-950" {...props} />
  ),
  em: ({ node: _node, ...props }) => <em className="italic text-gray-800" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="mt-4 list-disc space-y-2 pl-6" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="mt-4 list-decimal space-y-2 pl-6" {...props} />,
  li: ({ node: _node, ...props }) => <li className="text-gray-700" {...props} />,
  a: ({ node: _node, ...props }) => (
    <a className="font-semibold text-orange-700 underline hover:text-orange-800" {...props} />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      className="mt-6 rounded-r-lg border-l-4 border-orange-300 bg-orange-50 px-4 py-3 text-gray-700"
      {...props}
    />
  ),
  hr: ({ node: _node, ...props }) => <hr className="my-8 border-gray-200" {...props} />,
};

export function BlogArticleLayout({
  post,
  backHref,
  ctaHref,
  ctaLabel,
  headerRight,
}: {
  post: BlogPost;
  backHref: string;
  ctaHref: string;
  ctaLabel?: string;
  headerRight?: ReactNode;
}) {
  const displayLanguage = resolveBlogPostLanguage(post);
  const resolvedCtaLabel = ctaLabel ?? formatBlogPostCtaLabel(post);
  const secondaryCta = resolveBlogArticleSecondaryCta(post);
  const articleUrl = `https://www.chickytutor.com${backHref.replace(/\/$/, "")}/${post.slug}`;
  const contentLanguage = backHref.match(/^\/([a-z]{2})(?:\/|$)/)?.[1] ?? "en";
  const productBridge = resolveBlogProductBridge(post, contentLanguage);
  const demoVideo = contentLanguage === "en" ? resolveBlogArticleDemoVideo(post.slug) : undefined;
  const liveDemo = contentLanguage === "en" ? resolveBlogArticleLiveDemo(post.slug) : undefined;
  const extractedFaqs = extractFaqsFromMarkdown(post.content);
  const editorialCrossLinks = resolveBlogEditorialCrossLinks(post.slug);
  const relatedPracticeLinks = getBlogRelatedPracticeLinks(displayLanguage, post.slug);
  const publishedTime = toBlogDateTime(post.date, post.date);
  const modifiedTime = toBlogDateTime(post.updatedAt, post.date);
  const schemaDetails = resolveBlogArticleSchemaDetails(post);
  const articleJsonLd = serializeJsonLd({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: publishedTime,
    dateModified: modifiedTime,
    inLanguage: contentLanguage,
    articleSection: schemaDetails.articleSection,
    keywords: schemaDetails.keywords.join(", "),
    isAccessibleForFree: true,
    about: schemaDetails.about,
    url: articleUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articleUrl,
    },
    author: {
      "@type": "Organization",
      name: "ChickyTutor",
      url: "https://www.chickytutor.com",
    },
    publisher: {
      "@type": "Organization",
      name: "ChickyTutor",
      logo: {
        "@type": "ImageObject",
        url: "https://www.chickytutor.com/icon.png",
      },
    },
  });
  const breadcrumbJsonLd = serializeJsonLd(
    buildBreadcrumbListJsonLd([
      { name: "Home", path: "/" },
      { name: "Blog", path: backHref },
      { name: post.title, path: articleUrl },
    ])
  );
  const faqJsonLd =
    extractedFaqs.length > 0 ? serializeJsonLd(buildFaqPageJsonLd(extractedFaqs)) : null;
  const videoJsonLd = demoVideo
    ? serializeJsonLd({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: demoVideo.title,
        description: demoVideo.description,
        thumbnailUrl: [`https://www.chickytutor.com${demoVideo.poster}`],
        uploadDate: publishedTime,
        contentUrl: `https://www.chickytutor.com${demoVideo.src}`,
        embedUrl: `${articleUrl}#product-demo`,
      })
    : null;

  return (
    <BlogPageFrame maxWidth="max-w-5xl">
      <article>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleJsonLd }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }} />
        {faqJsonLd ? (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
        ) : null}
        {videoJsonLd ? (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: videoJsonLd }} />
        ) : null}

        <div className="flex items-center justify-between gap-4">
          <BlogBackLink href={backHref} />
          {headerRight}
        </div>

        <header className="mt-6 rounded-lg border border-orange-100 bg-[#fff9f1] p-6 shadow-sm sm:p-8">
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">
            <span className="rounded-full border border-orange-200 bg-white px-2.5 py-1 text-orange-700">
              {displayLanguage}
            </span>
            <span>{formatBlogDate(post.date)}</span>
          </div>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight text-gray-950 sm:text-5xl">
            {post.title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-600">{post.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              href={ctaHref}
              className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
            >
              {resolvedCtaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
            {secondaryCta ? (
              <Link
                href={secondaryCta.href}
                className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-5 py-2.5 text-sm font-semibold text-orange-800 transition-colors hover:border-orange-300 hover:bg-orange-50"
              >
                {secondaryCta.label}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            ) : null}
          </div>
        </header>

        {demoVideo ? (
          <section
            id="product-demo"
            aria-labelledby="blog-demo-video-title"
            className="mt-8 scroll-mt-24 rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-7"
          >
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(260px,0.8fr)] lg:items-center">
              <DemoVideo
                src={demoVideo.src}
                poster={demoVideo.poster}
                analyticsContext={{
                  demoId: "chickytutor-how-it-works4",
                  demoKind: "product_video",
                  pagePath: `${backHref.replace(/\/$/, "")}/${post.slug}`,
                  pageFamily: "blog_article",
                  placement: "below_article_header",
                }}
              />
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                  {demoVideo.kicker}
                </p>
                <h2
                  id="blog-demo-video-title"
                  className="mt-3 font-serif text-3xl font-semibold leading-tight text-gray-950"
                >
                  {demoVideo.title}
                </h2>
                <p className="mt-4 leading-7 text-gray-700">{demoVideo.description}</p>
                <ul className="mt-5 space-y-3 border-t border-orange-100 pt-5 text-sm leading-6 text-gray-700">
                  {demoVideo.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ) : null}

        <div className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <ReactMarkdown components={blogMarkdownComponents}>{post.content}</ReactMarkdown>
          {editorialCrossLinks.length > 0 ? (
            <nav aria-label="Related blog guides" className="mt-8 border-t border-gray-200 pt-6">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                Related guides
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {editorialCrossLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="rounded-lg border border-gray-200 bg-[#fbfaf7] p-4 transition-colors hover:border-orange-300 hover:bg-orange-50/50"
                  >
                    <span className="font-semibold text-gray-950">{link.label}</span>
                    <span className="mt-2 block text-sm leading-6 text-gray-600">
                      {link.description}
                    </span>
                  </Link>
                ))}
              </div>
            </nav>
          ) : null}
        </div>

        {liveDemo ? (
          <section className="mt-8 overflow-hidden rounded-lg border border-orange-200 bg-white shadow-sm">
            <div className="p-6 sm:p-8">
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                Live speaking demo
              </p>
              <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-gray-950">
                {liveDemo.title}
              </h2>
              <p className="mt-4 max-w-3xl leading-7 text-gray-700">{liveDemo.description}</p>
              <p className="mt-3 text-sm leading-6 text-gray-500">
                This starts a live practice session. It does not upload or read your lesson files.
              </p>
            </div>
            <div className="border-t border-orange-100 bg-[#0f0f14]">
              <SpeakingDemoIframe
                defaultTargetLanguageCode={liveDemo.targetLanguageCode}
                defaultInstructionLanguageCode={liveDemo.instructionLanguageCode}
                pageFamily="blog_between_lessons"
                pagePath={`/blog/${post.slug}`}
                placement="between_lesson_article"
                title={`${liveDemo.title} - live ChickyTutor demo`}
              />
            </div>
          </section>
        ) : null}

        <aside
          aria-labelledby={productBridge ? "blog-product-bridge-title" : undefined}
          className={cn(
            "mt-8 overflow-hidden shadow-sm",
            productBridge
              ? "relative rounded-2xl border border-slate-800 bg-slate-950 text-white"
              : "rounded-lg border border-orange-200 bg-[#fff7ed] p-6 sm:p-8"
          )}
        >
          {productBridge ? (
            <div className="relative grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)] lg:items-center">
              <div
                aria-hidden="true"
                className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-teal-300/10 blur-3xl"
              />
              <div className="relative">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300">
                  {productBridge.eyebrow}
                </p>
                <h2
                  id="blog-product-bridge-title"
                  className="mt-3 max-w-2xl font-serif text-3xl font-semibold leading-tight text-white"
                >
                  {productBridge.title}
                </h2>
                <p className="mt-4 max-w-2xl leading-7 text-slate-300">
                  {productBridge.description}
                </p>
                <Link
                  href={ctaHref}
                  className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-full bg-teal-300 px-5 text-sm font-bold text-slate-950 shadow-sm transition-colors hover:bg-teal-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-200 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                >
                  {productBridge.ctaLabel}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              </div>
              <ul className="relative space-y-3 rounded-xl border border-white/10 bg-white/[0.06] p-5 text-sm leading-6 text-slate-200">
                {productBridge.benefits.map((benefit) => (
                  <li key={benefit} className="flex gap-3">
                    <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-teal-300 text-slate-950">
                      <Check aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-orange-700">
                What to try next
              </p>
              <h2 className="mt-3 font-serif text-3xl font-semibold text-gray-950">
                Turn the article into a speaking session.
              </h2>
              <p className="mt-4 max-w-2xl leading-7 text-gray-700">
                Reading helps you choose a direction. ChickyTutor is where you practice saying it
                out loud with an AI tutor that can correct, repeat, and keep the conversation
                moving.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  href={ctaHref}
                  className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
                >
                  {resolvedCtaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {secondaryCta ? (
                  <Link
                    href={secondaryCta.href}
                    className="inline-flex items-center gap-2 rounded-full border border-orange-300 bg-white px-5 py-2.5 text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-50"
                  >
                    {secondaryCta.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>
            </>
          )}
          <div
            className={cn(
              "grid gap-3 sm:grid-cols-2",
              productBridge ? "border-t border-white/10 bg-white/[0.03] p-6 sm:p-8" : "mt-6"
            )}
          >
            {relatedPracticeLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "rounded-lg border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2",
                  productBridge
                    ? "border-white/10 bg-white/[0.05] hover:border-teal-300/60 hover:bg-white/[0.09] focus-visible:ring-teal-300"
                    : "border-orange-100 bg-white hover:border-orange-300 focus-visible:ring-orange-300"
                )}
              >
                <span
                  className={cn(
                    "inline-flex items-center gap-1 text-sm font-semibold",
                    productBridge ? "text-white" : "text-gray-950"
                  )}
                >
                  {link.label}
                  <ArrowUpRight
                    aria-hidden="true"
                    className={cn(
                      "h-3.5 w-3.5",
                      productBridge ? "text-teal-300" : "text-orange-500"
                    )}
                  />
                </span>
                <span
                  className={cn(
                    "mt-2 block text-sm leading-6",
                    productBridge ? "text-slate-300" : "text-gray-600"
                  )}
                >
                  {link.description}
                </span>
              </Link>
            ))}
          </div>
        </aside>
      </article>
    </BlogPageFrame>
  );
}
